import React, { useState, useEffect } from 'react';
import { useUnsavedGuard } from '../hooks/useUnsavedGuard';
import { Link } from 'react-router-dom';
import { 
  ArrowLeft, 
  Save, 
  AlertTriangle, 
  CheckCircle, 
  XCircle,
  Shield,
  Calendar,
  User,
  Camera,
  Bell,
  Package
} from 'lucide-react';
import { api } from '@/utils/api';
// Cash hardening: LOCAL calendar-day default for date inputs (the UTC-day
// idiom showed tomorrow's date to US evening users).
import { toLocalDateInputValue } from '@/utils/calendarDate';

interface PPEItem {
  assetId: number;
  assetName: string;
  assetType: string;
  serialNumber: string;
  lastInspection?: string;
  status: string;
}

interface InspectionPoint {
  id: string;
  label: string;
  category: string;
  criticality: 'critical' | 'important' | 'standard';
}

type InspectionStatus = 'PASS' | 'WARN' | 'FAIL' | 'NOT_CHECKED';

interface InspectionResult {
  pointId: string;
  status: InspectionStatus;
  notes: string;
  photoUrl?: string;
}

const PPE_INSPECTION_POINTS: InspectionPoint[] = [
  // FULL BODY HARNESS
  { id: 'harness_webbing', label: 'Webbing - No cuts, burns, or abrasions', category: 'Full Body Harness', criticality: 'critical' },
  { id: 'harness_stitching', label: 'Stitching - All intact, no broken threads', category: 'Full Body Harness', criticality: 'critical' },
  { id: 'harness_d_ring', label: 'D-Ring - No cracks, bends, or corrosion', category: 'Full Body Harness', criticality: 'critical' },
  { id: 'harness_buckles', label: 'Buckles - Engage properly, no damage', category: 'Full Body Harness', criticality: 'critical' },
  { id: 'harness_grommets', label: 'Grommets - Secure, no tears', category: 'Full Body Harness', criticality: 'important' },
  { id: 'harness_labels', label: 'Labels - Legible, not missing', category: 'Full Body Harness', criticality: 'standard' },

  // Y-LANYARD (SHOCK ABSORBING)
  { id: 'lanyard_webbing', label: 'Webbing - No cuts, fraying, or damage', category: 'Y-Lanyard', criticality: 'critical' },
  { id: 'lanyard_shock', label: 'Shock Absorber - Not deployed, pack intact', category: 'Y-Lanyard', criticality: 'critical' },
  { id: 'lanyard_hooks', label: 'Snap Hooks - Gate closes properly, no cracks', category: 'Y-Lanyard', criticality: 'critical' },
  { id: 'lanyard_stitching', label: 'Stitching - All intact at connection points', category: 'Y-Lanyard', criticality: 'critical' },
  { id: 'lanyard_corrosion', label: 'Hardware - No rust or corrosion', category: 'Y-Lanyard', criticality: 'important' },

  // ROPE GRAB / ROPE ADJUSTER
  { id: 'ropegrab_body', label: 'Body - No cracks, dents, or deformation', category: 'Rope Grab', criticality: 'critical' },
  { id: 'ropegrab_cam', label: 'Cam/Locking mechanism - Engages and releases properly', category: 'Rope Grab', criticality: 'critical' },
  { id: 'ropegrab_spring', label: 'Spring tension - Adequate return force', category: 'Rope Grab', criticality: 'critical' },
  { id: 'ropegrab_connection', label: 'Connection point - No wear, cracks, or corrosion', category: 'Rope Grab', criticality: 'critical' },
  { id: 'ropegrab_labels', label: 'Labels - Legible, rope size compatible', category: 'Rope Grab', criticality: 'important' },
  { id: 'ropegrab_channel', label: 'Rope channel - Clean, no burrs or damage', category: 'Rope Grab', criticality: 'important' },

  // VERTICAL LIFELINE (ROPE)
  { id: 'lifeline_rope', label: 'Rope - No cuts, abrasions, glazing, or soft spots', category: 'Vertical Lifeline', criticality: 'critical' },
  { id: 'lifeline_termination', label: 'Termination/Splice - Intact, no pulled strands', category: 'Vertical Lifeline', criticality: 'critical' },
  { id: 'lifeline_thimble', label: 'Thimble/Eye - Secure, no deformation', category: 'Vertical Lifeline', criticality: 'critical' },
  { id: 'lifeline_snap', label: 'Snap hook (if equipped) - Gate closes, locks properly', category: 'Vertical Lifeline', criticality: 'critical' },
  { id: 'lifeline_length', label: 'Length marking - Legible, correct for application', category: 'Vertical Lifeline', criticality: 'important' },
  { id: 'lifeline_contamination', label: 'Contamination - No oil, chemicals, or paint', category: 'Vertical Lifeline', criticality: 'important' },

  // SELF-RETRACTING LIFELINE (SRL)
  { id: 'srl_housing', label: 'Housing - No cracks, dents, or damage', category: 'Self-Retracting Lifeline', criticality: 'critical' },
  { id: 'srl_line', label: 'Lifeline (cable/webbing) - No kinks, fraying, or damage', category: 'Self-Retracting Lifeline', criticality: 'critical' },
  { id: 'srl_retraction', label: 'Retraction - Line retracts smoothly under tension', category: 'Self-Retracting Lifeline', criticality: 'critical' },
  { id: 'srl_braking', label: 'Braking - Locks when pulled sharply', category: 'Self-Retracting Lifeline', criticality: 'critical' },
  { id: 'srl_hook', label: 'Snap hook - Gate closes, double-locking works', category: 'Self-Retracting Lifeline', criticality: 'critical' },
  { id: 'srl_swivel', label: 'Swivel (if equipped) - Rotates freely', category: 'Self-Retracting Lifeline', criticality: 'important' },
  { id: 'srl_indicator', label: 'Fall indicator - Not deployed/activated', category: 'Self-Retracting Lifeline', criticality: 'critical' },
  { id: 'srl_labels', label: 'Labels - Legible, inspection date current', category: 'Self-Retracting Lifeline', criticality: 'important' },

  // GRILLION (POSITIONING LANYARD)
  { id: 'grillion_rope', label: 'Rope - No cuts, glazing, or soft spots', category: 'Grillion', criticality: 'critical' },
  { id: 'grillion_adjuster', label: 'Adjuster - Moves smoothly, locks securely', category: 'Grillion', criticality: 'critical' },
  { id: 'grillion_hooks', label: 'Hooks - Gate function, locking mechanism works', category: 'Grillion', criticality: 'critical' },
  { id: 'grillion_stitching', label: 'Splicing/Stitching - Eye splices intact', category: 'Grillion', criticality: 'critical' },

  // CARABINER / CONNECTOR
  { id: 'carabiner_body', label: 'Body - No cracks, distortion, or sharp edges', category: 'Carabiner', criticality: 'critical' },
  { id: 'carabiner_gate', label: 'Gate - Opens and closes smoothly', category: 'Carabiner', criticality: 'critical' },
  { id: 'carabiner_lock', label: 'Locking mechanism - Auto-locks, manual unlocks properly', category: 'Carabiner', criticality: 'critical' },
  { id: 'carabiner_spring', label: 'Spring - Adequate tension, gate closes fully', category: 'Carabiner', criticality: 'critical' },
  { id: 'carabiner_marking', label: 'Markings - Load rating visible', category: 'Carabiner', criticality: 'important' },

  // SPREADER BAR
  { id: 'spreader_structure', label: 'Main Structure - No bends, cracks, or deformation', category: 'Spreader Bar', criticality: 'critical' },
  { id: 'spreader_welds', label: 'Welds - No cracks or separation', category: 'Spreader Bar', criticality: 'critical' },
  { id: 'spreader_shackles', label: 'Shackle Points - Pins secure, threads good', category: 'Spreader Bar', criticality: 'critical' },
  { id: 'spreader_markings', label: 'Load Markings - WLL clearly visible', category: 'Spreader Bar', criticality: 'important' },
  { id: 'spreader_paint', label: 'Paint/Coating - No excessive rust', category: 'Spreader Bar', criticality: 'standard' },

  // HELMET
  { id: 'helmet_shell', label: 'Shell - No cracks, dents, or UV damage', category: 'Hard Hat', criticality: 'critical' },
  { id: 'helmet_suspension', label: 'Suspension - All straps intact, no wear', category: 'Hard Hat', criticality: 'critical' },
  { id: 'helmet_chinstrap', label: 'Chin Strap - Present and functional', category: 'Hard Hat', criticality: 'important' },

  // SAFETY GLASSES
  { id: 'glasses_lenses', label: 'Lenses - No scratches affecting vision', category: 'Safety Glasses', criticality: 'important' },
  { id: 'glasses_frame', label: 'Frame - Not bent or broken', category: 'Safety Glasses', criticality: 'important' },

  // GLOVES
  { id: 'gloves_leather', label: 'Leather - No tears or excessive wear', category: 'Work Gloves', criticality: 'standard' },
  { id: 'gloves_seams', label: 'Seams - All intact', category: 'Work Gloves', criticality: 'standard' },
];

// Personal PPE categories for "Full Gear Check" mode (climber's daily check)
const PERSONAL_PPE_CATEGORIES = [
  'Work Boots',
  'Full Body Harness',
  'Y-Lanyard',
  'Rope Grab',
  'Grillion',
  'Self-Retracting Lifeline',
  'Carabiner',
  'Hard Hat',
  'Safety Glasses',
  'Work Gloves',
];

type InspectionMode = 'single' | 'full';

// BP2: explicit AssetType → checklist-category mapping. Type-first, so a
// helmet named "Big Blue Bucket" still gets the hard-hat checklist; the
// name-guessing below stays only as fallback for free-form assets.
const ASSET_TYPE_TO_CATEGORY: Record<string, string> = {
  PPE_HARNESS: 'full body harness',
  PPE_HELMET: 'hard hat',
  PPE_GLOVES: 'work gloves',
  PPE_BOOTS: 'work boots',
  FALL_PROTECTION: '', // fall-protection hardware resolves by name/subtype below
};

const WORK_BOOTS_POINTS: InspectionPoint[] = [
  { id: 'boots_leather', label: 'Leather/upper - No cuts, tears, or burns', category: 'Work Boots', criticality: 'critical' },
  { id: 'boots_sole', label: 'Soles - Intact with tread; no separation or deep cracks', category: 'Work Boots', criticality: 'critical' },
  { id: 'boots_toe', label: 'Toe cap / protection - Present and undamaged', category: 'Work Boots', criticality: 'critical' },
  { id: 'boots_laces', label: 'Laces, hooks, and eyelets - Functional and secure', category: 'Work Boots', criticality: 'standard' },
  { id: 'boots_fit', label: 'Fit and condition suitable for climbing/work at height', category: 'Work Boots', criticality: 'standard' },
];

const PPE_INSPECTION_POINTS_WITH_BOOTS = [...PPE_INSPECTION_POINTS, ...WORK_BOOTS_POINTS];

// SOL-D1-2 (wd-fix-delta-sol-2026-10-05 + Cash plan 3a): pointId → checklist
// category lookup, used to fan a Full Gear Check out into one record per
// tracked asset (each record carries only that category's checked points).
const POINT_CATEGORY: Record<string, string> = {};
for (const p of PPE_INSPECTION_POINTS_WITH_BOOTS) POINT_CATEGORY[p.id] = p.category;

// SOL-D1-2: category → tracked asset type, derived from the page's OWN
// ASSET_TYPE_TO_CATEGORY mapping (inverted) — this mapping is not ambiguous:
// Work Boots/PPE_BOOTS, Full Body Harness/PPE_HARNESS, Hard Hat/PPE_HELMET,
// Work Gloves/PPE_GLOVES. Fall-protection hardware (Y-Lanyard, Rope Grab,
// Grillion, Self-Retracting Lifeline, Carabiner) is FALL_PROTECTION type and
// resolves by name keywords, the same heuristic single-item mode already uses.
const CATEGORY_TO_ASSET_TYPE: Record<string, string> = {
  'Work Boots': 'PPE_BOOTS',
  'Full Body Harness': 'PPE_HARNESS',
  'Hard Hat': 'PPE_HELMET',
  'Work Gloves': 'PPE_GLOVES',
};
const FALL_PROTECTION_CATEGORY_KEYWORDS: Record<string, string[]> = {
  'Y-Lanyard': ['y-lanyard', 'y lanyard', 'lanyard'],
  'Rope Grab': ['rope grab', 'ropegrab', 'grab'],
  'Grillion': ['grillion', 'positioning'],
  'Self-Retracting Lifeline': ['srl', 'retract', 'self-retracting'],
  'Carabiner': ['carabiner', 'connector'],
};

export function PPEInspectionPage() {
  const [ppeItems, setPpeItems] = useState<PPEItem[]>([]);
  const [selectedItem, setSelectedItem] = useState<PPEItem | null>(null);
  const [inspectionResults, setInspectionResults] = useState<Record<string, InspectionResult>>({});
  // S1: warn before unload while an inspection is in progress (answers unsaved)
  useUnsavedGuard(Object.keys(inspectionResults).length > 0);
  // DE-005 (user-POV 2026-10-06): Inspector Name defaults to the LOGGED-IN
  // user's name — the authenticated identity is already known and is what
  // other inspection surfaces record. The field stays editable (a worker may
  // record on behalf of someone else who actually did the check).
  const [inspectorName, setInspectorName] = useState(() => {
    try {
      const stored = JSON.parse(localStorage.getItem('user') || 'null');
      const name = `${stored?.firstName ?? ''} ${stored?.lastName ?? ''}`.trim();
      return name || '';
    } catch {
      return '';
    }
  });
  // SOL-D1-2: the visible Inspection Date input was disabled and its value
  // discarded (payload hard-coded new Date().toISOString()) — the field is
  // now bound so the value the user sees is exactly what gets posted.
  const [inspectionDate, setInspectionDate] = useState(() => toLocalDateInputValue());
  const [saving, setSaving] = useState(false);
  const [loading, setLoading] = useState(true);
  const [inspectionMode, setInspectionMode] = useState<InspectionMode>('single');
  // Fix 7 (UAT): recent inspections list — workers fetch their own (/my),
  // foreman+ see the company-wide list. Replaces the blind POST with
  // submit → refresh feedback.
  const [recentInspections, setRecentInspections] = useState<any[]>([]);
  // Luna round-1 LOW-6: track load completion so the empty state renders
  // after a fetch that found nothing (and only then — not during load).
  const [recentLoaded, setRecentLoaded] = useState(false);

  const loadRecentInspections = async () => {
    try {
      const userRaw = localStorage.getItem('user');
      const user = userRaw ? JSON.parse(userRaw) : null;
      const isWorker = user?.role === 'WORKER' || user?.role === 'CREW_MEMBER';
      const response = await api.get(isWorker ? '/ppe-inspections/my' : '/ppe-inspections');
      setRecentInspections(response.data?.data ?? []);
    } catch (error) {
      console.error('Error loading recent inspections:', error);
    } finally {
      setRecentLoaded(true);
    }
  };

  useEffect(() => {
    loadPPEItems();
    loadRecentInspections();
  }, []);

  const loadPPEItems = async () => {
    try {
      // BP2: load EVERY PPE asset type, not just FALL_PROTECTION — harnesses,
      // helmets, gloves and boots are PPE and must be inspectable here.
      const PPE_TYPES = ['PPE_HARNESS', 'PPE_HELMET', 'PPE_GLOVES', 'PPE_BOOTS', 'FALL_PROTECTION'];
      const response = await api.get(`/assets?type=${PPE_TYPES.join(',')}`);
      // Map API response to expected interface, handling different field names
      const items = (response.data.data || []).map((item: any) => ({
        assetId: item.id || item.assetId,
        assetName: item.name || item.assetName || 'PPE Item',
        assetType: item.type || item.assetType || 'FALL_PROTECTION',
        serialNumber: item.serialNumber || 'N/A',
        lastInspection:
          typeof item.lastInspection === 'string'
            ? item.lastInspection
            : item.lastInspection?.date || item.lastInspectionDate || null,
        status: item.status || 'AVAILABLE',
      }));
      setPpeItems(items);
    } catch (error) {
      console.error('Error loading PPE:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleStatusChange = (pointId: string, status: InspectionStatus) => {
    setInspectionResults(prev => ({
      ...prev,
      [pointId]: {
        ...prev[pointId],
        pointId,
        status,
        notes: prev[pointId]?.notes || '',
      }
    }));
  };

  const handleNotesChange = (pointId: string, notes: string) => {
    setInspectionResults(prev => ({
      ...prev,
      [pointId]: {
        ...prev[pointId],
        pointId,
        status: prev[pointId]?.status || 'NOT_CHECKED',
        notes,
      }
    }));
  };

  const handleSubmit = async () => {
    // Validation based on mode
    if (inspectionMode === 'single' && !selectedItem) {
      alert('Please select a PPE item to inspect');
      return;
    }

    if (!inspectorName.trim()) {
      alert('Please enter inspector name');
      return;
    }

    const results = Object.values(inspectionResults);
    const failedItems = results.filter(r => r.status === 'FAIL');
    const warnItems = results.filter(r => r.status === 'WARN');

    if (failedItems.length === 0 && warnItems.length === 0 && results.length === 0) {
      alert('Please complete at least one inspection point');
      return;
    }

    setSaving(true);
    try {
      // SOL-D1-2: Full Gear Check now SAVES. The old code posted a single
    // payload with `assetId: null`, but the API requires a numeric assetId —
    // the mode had never saved. Per the v1.5 per-item philosophy we fan out
    // ONE POST /ppe-inspections per TRACKED personal-PPE asset the user holds
    // whose type matches each checked category, each record carrying only
    // that category's checked points. Categories with no tracked gear are
    // reported honestly — never silently dropped.
    if (inspectionMode === 'full') {
      const checkedCategories = Array.from(new Set(
        results.map((r) => POINT_CATEGORY[r.pointId]).filter(Boolean) as string[],
      ));
      const noGearCategories: string[] = [];
      const perAsset: { assetId: number; results: InspectionResult[]; overallStatus: 'PASS' | 'WARN' | 'FAIL' }[] = [];
      for (const category of checkedCategories) {
        const fixedType = CATEGORY_TO_ASSET_TYPE[category];
        const keywords = FALL_PROTECTION_CATEGORY_KEYWORDS[category] || [];
        const matchingGear = fixedType
          ? ppeItems.filter((i) => i.assetType === fixedType)
          : ppeItems.filter((i) =>
              i.assetType === 'FALL_PROTECTION' &&
              keywords.some((k) => `${i.assetType} ${i.assetName}`.toLowerCase().includes(k)));
        if (matchingGear.length === 0) {
          noGearCategories.push(category);
          continue;
        }
        const catResults = results.filter((r) => POINT_CATEGORY[r.pointId] === category);
        const overall: 'PASS' | 'WARN' | 'FAIL' = catResults.some((r) => r.status === 'FAIL') ? 'FAIL'
          : catResults.some((r) => r.status === 'WARN') ? 'WARN' : 'PASS';
        for (const gear of matchingGear) {
          perAsset.push({ assetId: gear.assetId, results: catResults, overallStatus: overall });
        }
      }
      if (perAsset.length === 0) {
        alert(
          `Full Gear Check could not be saved — none of the ${checkedCategories.length} checked categories match tracked gear you hold.\n` +
          'Why: every PPE record must point at a tracked asset, and nothing in your Asset Tracker matches the categories you checked.\n' +
          'How to fix: ask your manager to add your PPE to the Asset Tracker, or use Single Item mode to inspect a tracked asset.'
        );
        return;
      }
      setSaving(true);
      try {
        // REAUDIT-002: capture the per-record save responses — the fan-out used
        // to discard them, so the final alert could not report real delivery.
        const saveResponses = await Promise.all(perAsset.map((p) => api.post('/ppe-inspections', {
          assetId: p.assetId,
          inspectionType: 'FULL_GEAR_CHECK',
          inspectorName,
          inspectionDate,
          // D19-002: the inspector's device timezone — same key as the
          // single-item path. The server parses a date-only value at noon in
          // the USER timezone; without it a far-east user's picked day stored
          // and rendered one day off.
          timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
          results: p.results,
          overallStatus: p.overallStatus,
        })));
        // The server creates the FAIL/WARN notification per record, so no
        // extra client-side /notifications call is fanned out here.
        const gearNote = noGearCategories.length > 0
          ? `\n⚠️ ${noGearCategories.length} of ${checkedCategories.length} checked categories had no tracked gear — nothing recorded for them; ask your manager to add your gear (${noGearCategories.join(', ')}).`
          : '';
        // REAUDIT-002: aggregate the REAL alert outcomes from the save
        // responses. Only 'SENT' records carry a delivery claim (their
        // notificationDelivery counts are summed); UNDELIVERED/FAILED records
        // are never claimed as delivered and never silently dropped.
        let deliveredPushed = 0;
        let deliveredEmailed = 0;
        let undelivered = 0;
        // NL-RECHECK-003: records whose alert outcome is UNRESOLVED (an
        // attempt still in flight, or an unconfirmed dispatch) are surfaced
        // too — never silently dropped and never claimed as alerted.
        let unresolved = 0;
        for (const r of saveResponses) {
          const state = r.data?.notificationState as string | undefined;
          const reason = r.data?.notificationReason as string | undefined;
          if (state === 'SENT') {
            deliveredPushed += r.data?.notificationDelivery?.pushed ?? 0;
            deliveredEmailed += r.data?.notificationDelivery?.emailed ?? 0;
          } else if (state === 'SKIPPED_DUPLICATE' && reason === 'IN_FLIGHT') {
            unresolved += 1;
          } else if (state === 'UNDELIVERED' || state === 'FAILED') {
            if (reason === 'DISPATCH_UNCONFIRMED') unresolved += 1;
            else undelivered += 1;
          }
        }
        const deliveryLine = deliveredPushed + deliveredEmailed > 0
          ? `\n🔔 ${deliveredPushed} push alert(s) delivered · 📧 ${deliveredEmailed} email(s) sent to your safety manager.`
          : '';
        const undeliveredLine = undelivered > 0
          ? `\n⚠️ ${undelivered} record(s) manager alert could not be delivered — tell your safety manager directly.`
          : '';
        const unresolvedLine = unresolved > 0
          ? `\n⚠️ ${unresolved} record(s) manager alert is not yet confirmed (an attempt may still be in progress) — check with your safety manager directly.`
          : '';
        alert(`✅ Full Gear Check saved — ${perAsset.length} tracked item${perAsset.length === 1 ? '' : 's'} recorded.${gearNote}${deliveryLine}${undeliveredLine}${unresolvedLine}`);
        setInspectionResults({});
        loadPPEItems();
        loadRecentInspections();
      } catch (error: any) {
        console.error('Error saving full gear check:', error);
        alert('Failed to save Full Gear Check. Some records may not have saved — check Recent inspections and try again.');
      } finally {
        setSaving(false);
      }
      return;
    }

    const item = selectedItem as PPEItem; // guarded above for single mode
    const inspectionData = {
      // SOL-D1-2: assetId is a REQUIRED number on the API — the old
      // `selectedItem?.assetId || null` only worked because full mode (which
      // has no item) silently posted null and 400'd forever.
      assetId: item.assetId,
      inspectionType: 'SINGLE_ITEM',
      inspectorName,
      // SOL-D1-2: bound to the Inspection Date input — was hard-coded
      // new Date().toISOString(), discarding whatever the field showed.
      inspectionDate,
      // LUNA18-PPE-001: the inspector's device timezone — the server parses a
      // date-only value at noon in the USER timezone and runs the future check
      // against the user's local calendar day.
      timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
      results: Object.values(inspectionResults),
      overallStatus: failedItems.length > 0 ? 'FAIL' : warnItems.length > 0 ? 'WARN' : 'PASS',
    };

      const resp = await api.post('/ppe-inspections', inspectionData);

      // If any items failed or warned, surface the manager-alert outcome.
      // POSTFIX-001: the server already dispatched the durable manager alert
      // during THIS save — no second /notifications fan-out here. The delivery
      // claim comes from the save response itself (notificationState +
      // notificationDelivery), so the user is told the truth.
      if (failedItems.length > 0 || warnItems.length > 0) {
        const state = resp.data?.notificationState as 'SENT' | 'UNDELIVERED' | 'FAILED' | 'SKIPPED_DUPLICATE' | undefined;
        // NL-RECHECK-003: the server's additive reason distinguishes claim
        // loss (an attempt still in flight — it can still fail) and
        // unconfirmed dispatches from genuinely delivered/failed ones.
        const reason = resp.data?.notificationReason as string | undefined;
        // REAUDIT-002: 'UNDELIVERED' (dispatch ran but reached nobody) is
        // exactly like 'FAILED' for the user — claim NO delivery counts and
        // tell them to inform their safety manager.
        if (reason === 'DISPATCH_UNCONFIRMED') {
          // NL-RECHECK-003: the attempt did not settle in time — unknown is
          // not delivered, and "could not be delivered" is equally unproven.
          alert(`✅ Inspection saved.\n⚠️ ${failedItems.length + warnItems.length} issue(s) detected.\n⚠️ The manager alert is unconfirmed — tell your safety manager directly.`);
        } else if (state === 'FAILED' || state === 'UNDELIVERED') {
          // Honest failure note: never claim counts that were not delivered.
          alert(`✅ Inspection saved.\n⚠️ ${failedItems.length + warnItems.length} issue(s) detected.\n⚠️ The manager alert could not be delivered — tell your safety manager directly.`);
        } else if (state === 'SKIPPED_DUPLICATE' && reason === 'IN_FLIGHT') {
          // NL-RECHECK-003: a claim loser only knows another attempt is in
          // flight — never say the manager "was already alerted earlier".
          alert(`✅ Inspection saved.\n⚠️ ${failedItems.length + warnItems.length} issue(s) detected.\nℹ️ Another alert attempt for this inspection is still in progress and not yet confirmed — check with your safety manager directly.`);
        } else if (state === 'SKIPPED_DUPLICATE') {
          alert(`✅ Inspection saved.\n⚠️ ${failedItems.length + warnItems.length} issue(s) detected.\nℹ️ This duplicate save was already alerted to your safety manager earlier — no new alert was sent.`);
        } else {
          const pushed = resp.data?.notificationDelivery?.pushed ?? 0;
          const emailed = resp.data?.notificationDelivery?.emailed ?? 0;
          alert(`✅ Inspection saved!\n⚠️ ${failedItems.length + warnItems.length} issue(s) detected.\n🔔 ${pushed} push alert(s) delivered · 📧 ${emailed} email(s) sent to your safety manager.`);
        }
      } else {
        alert(`✅ Inspection saved! All items passed.`);
      }

      // Reset form
      setInspectionResults({});
      setSelectedItem(null);
      loadPPEItems(); // Refresh list
      loadRecentInspections(); // Fix 7 (UAT): refresh the recent-inspections list after submit
    } catch (error: any) {
      console.error('Error saving inspection:', error);
      alert('Failed to save inspection. Please try again.');
    } finally {
      setSaving(false);
    }
  };

  const getOverallStatus = () => {
    const results = Object.values(inspectionResults);
    const failCount = results.filter(r => r.status === 'FAIL').length;
    const warnCount = results.filter(r => r.status === 'WARN').length;
    const passCount = results.filter(r => r.status === 'PASS').length;
    
    return { failCount, warnCount, passCount, total: results.length };
  };

  const status = getOverallStatus();
  
  // Get relevant inspection points based on mode
  const relevantPoints = (() => {
    // FULL GEAR CHECK MODE: Show all personal PPE categories
    if (inspectionMode === 'full') {
      return PPE_INSPECTION_POINTS_WITH_BOOTS.filter(point => 
        PERSONAL_PPE_CATEGORIES.includes(point.category)
      );
    }
    
    // SINGLE ITEM MODE: Show ONLY the selected item's inspection points
    if (!selectedItem) return [];
    
    // Check both asset type AND name for matching (name often has the actual equipment type)
    const typeField = selectedItem.assetType || (selectedItem as any).type || '';
    const nameField = selectedItem.assetName || (selectedItem as any).name || '';
    const searchText = `${typeField} ${nameField}`.toLowerCase();
    
    // Debug: log what we're matching against
    console.log('PPE Filter - Asset:', nameField, 'Type:', typeField, 'Search:', searchText);
    
    // Determine which category to show based on asset name/type
    let matchedCategory: string | null = null;

    // BP2: explicit type mapping wins before any name guessing.
    const explicitCategory = ASSET_TYPE_TO_CATEGORY[typeField];
    if (explicitCategory) matchedCategory = explicitCategory;
    
    if (matchedCategory) {
      // explicit mapping already set
    }
    else if (searchText.includes('harness')) matchedCategory = 'full body harness';
    else if (searchText.includes('y-lanyard') || searchText.includes('y lanyard') || 
             (searchText.includes('lanyard') && (searchText.includes('shock') || searchText.includes('twin')))) matchedCategory = 'y-lanyard';
    else if (searchText.includes('rope grab') || searchText.includes('ropegrab') || searchText.includes('grab')) matchedCategory = 'rope grab';
    else if (searchText.includes('lifeline') && (searchText.includes('vertical') || searchText.includes('rope'))) matchedCategory = 'vertical lifeline';
    else if (searchText.includes('lifeline') && !searchText.includes('retract') && !searchText.includes('srl')) matchedCategory = 'vertical lifeline';
    else if (searchText.includes('srl') || searchText.includes('retract') || searchText.includes('self-retracting')) matchedCategory = 'self-retracting lifeline';
    else if (searchText.includes('grillion') || searchText.includes('positioning')) matchedCategory = 'grillion';
    else if (searchText.includes('carabiner') || searchText.includes('connector')) matchedCategory = 'carabiner';
    else if (searchText.includes('spreader')) matchedCategory = 'spreader bar';
    else if (searchText.includes('helmet') || searchText.includes('hard hat')) matchedCategory = 'hard hat';
    else if (searchText.includes('glasses') || searchText.includes('eyewear')) matchedCategory = 'safety glasses';
    else if (searchText.includes('gloves')) matchedCategory = 'work gloves';
    
    console.log('PPE Filter - Matched category:', matchedCategory);
    
    // If we found a specific category, show only those points
    if (matchedCategory) {
      return PPE_INSPECTION_POINTS_WITH_BOOTS.filter(point => 
        point.category.toLowerCase() === matchedCategory
      );
    }
    
    // Fallback: try to find any category that matches words in the asset name
    const matchingPoints = PPE_INSPECTION_POINTS.filter(point => {
      const cat = point.category.toLowerCase();
      // Check if any word from the category appears in the search text
      const catWords = cat.split(' ').filter(w => w.length > 2);
      return catWords.some(word => searchText.includes(word));
    });
    
    if (matchingPoints.length > 0) {
      console.log('PPE Filter - Found via word match:', matchingPoints.length, 'points');
      return matchingPoints;
    }
    
    // Last resort: show all points for this asset (better than showing nothing)
    console.log('PPE Filter - No match found, showing all points');
    return PPE_INSPECTION_POINTS_WITH_BOOTS;
  })();

  const groupedPoints = relevantPoints.reduce((acc, point) => {
    if (!acc[point.category]) acc[point.category] = [];
    acc[point.category].push(point);
    return acc;
  }, {} as Record<string, InspectionPoint[]>);

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Fix 7 (UAT): recent inspections list with result badge and date.
          Luna round-1 LOW-6: the empty state previously sat INSIDE the
          length>0 branch and was unreachable. The card now renders whenever
          the list is loaded (with a loading state while fetching) so an
          empty history shows its message. */}
      {recentLoaded && (
        <div className="container mx-auto px-6 mt-4">
          <div className="bg-white rounded-xl shadow border border-gray-100 p-4">
            <h2 className="text-sm font-semibold text-gray-900 mb-2 flex items-center gap-2">
              <Calendar className="h-4 w-4 text-blue-600" /> Recent inspections
            </h2>
            {recentInspections.length === 0 ? (
              <p className="text-sm text-gray-500">No inspections recorded yet. Your submitted inspections will appear here.</p>
            ) : (
              <ul className="divide-y divide-gray-100">
                {recentInspections.slice(0, 10).map((insp: any) => {
                  const badge = insp.status === 'PASS'
                    ? 'bg-green-100 text-green-700'
                    : insp.status === 'NEEDS_REPAIR'
                      ? 'bg-amber-100 text-amber-700'
                      : 'bg-red-100 text-red-700';
                  return (
                    <li key={insp.id} className="flex items-center justify-between py-2 text-sm">
                      <span className="text-gray-700">
                        {insp.asset?.name ?? 'PPE item'}{insp.asset?.serialNumber ? ` (${insp.asset.serialNumber})` : ''}
                      </span>
                      <span className="flex items-center gap-2">
                        <span className={`text-xs px-2 py-0.5 rounded-full font-medium ${badge}`}>{insp.status}</span>
                        {insp.retracted && (
                          <span className="text-xs px-2 py-0.5 rounded-full font-medium bg-gray-100 text-gray-600">
                            Withdrawn
                          </span>
                        )}
                        <span className="text-xs text-gray-500">
                          {insp.date ? new Date(insp.date).toLocaleDateString('en-US') : ''}
                        </span>
                      </span>
                    </li>
                  );
                })}
              </ul>
            )}
          </div>
        </div>
      )}
      {/* Header */}
      <div className="bg-white border-b border-gray-200">
        <div className="container mx-auto px-6 py-4">
          <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
            <div className="flex items-center gap-4">
              <Link to="/dashboard" className="text-gray-600 hover:text-gray-900">
                <ArrowLeft className="h-6 w-6" />
              </Link>
              <div>
                <h1 className="text-xl md:text-2xl font-bold text-gray-900 flex items-center gap-2">
                  <Shield className="h-7 w-7 text-blue-600" />
                  PPE Inspection Checklist
                </h1>
                <p className="text-sm text-gray-600 mt-1">
                  {inspectionMode === 'full' 
                    ? 'Complete daily check of all personal protective equipment'
                    : 'Individual equipment inspection'}
                </p>
              </div>
            </div>
            {(selectedItem || inspectionMode === 'full') && (
              <button
                onClick={handleSubmit}
                disabled={saving || status.total === 0}
                className="px-6 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition flex items-center gap-2 font-semibold disabled:opacity-50"
              >
                <Save className="h-5 w-5" />
                {saving ? 'Saving...' : 'Save Inspection'}
              </button>
            )}
          </div>
        </div>
      </div>

      <div className="container mx-auto px-6 py-8">
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
          {/* LEFT: Mode Toggle & PPE Item Selection */}
          <div className="lg:col-span-1">
            {/* Inspection Mode Toggle */}
            <div className="bg-white rounded-lg shadow-lg p-6 mb-6">
              <h2 className="text-lg font-bold text-gray-900 mb-4">Inspection Type</h2>
              <div className="grid grid-cols-2 gap-2">
                <button
                  onClick={() => {
                    setInspectionMode('single');
                    setSelectedItem(null);
                    setInspectionResults({});
                  }}
                  className={`p-3 rounded-lg border-2 text-center transition ${
                    inspectionMode === 'single'
                      ? 'border-blue-600 bg-blue-50 text-blue-700'
                      : 'border-gray-200 text-gray-600 hover:border-blue-300'
                  }`}
                >
                  <Package className="h-6 w-6 mx-auto mb-1" />
                  <div className="font-semibold text-sm">Single Item</div>
                  <div className="text-xs opacity-70">Inspect one asset</div>
                </button>
                <button
                  onClick={() => {
                    setInspectionMode('full');
                    setSelectedItem(null);
                    setInspectionResults({});
                  }}
                  className={`p-3 rounded-lg border-2 text-center transition ${
                    inspectionMode === 'full'
                      ? 'border-green-600 bg-green-50 text-green-700'
                      : 'border-gray-200 text-gray-600 hover:border-green-300'
                  }`}
                >
                  <Shield className="h-6 w-6 mx-auto mb-1" />
                  <div className="font-semibold text-sm">Full Gear Check</div>
                  <div className="text-xs opacity-70">All personal PPE</div>
                </button>
              </div>
              
              {inspectionMode === 'full' && (
                <div className="mt-4 p-3 bg-green-50 border border-green-200 rounded-lg text-sm text-green-800">
                  <p className="font-medium">📋 Full Gear Check Mode</p>
                  <p className="text-xs mt-1">Inspect all your personal PPE: harness, lanyard, rope grab, helmet, glasses, gloves, and more.</p>
                </div>
              )}
              
              {inspectionMode === 'single' && (
                <div className="mt-4 p-3 bg-blue-50 border border-blue-200 rounded-lg text-sm text-blue-800">
                  <p className="font-medium">🔧 Single Item Mode</p>
                  <p className="text-xs mt-1">Select a specific asset below to inspect only that equipment.</p>
                </div>
              )}
            </div>

            <div className="bg-white rounded-lg shadow-lg p-6">
              <h2 className="text-xl font-bold text-gray-900 mb-4">
                {inspectionMode === 'full' ? 'Inspector Info' : 'Select PPE Item'}
              </h2>
              
              {/* Inspector Name */}
              <div className="mb-6">
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  <User className="h-4 w-4 inline mr-1" />
                  Inspector Name
                </label>
                <input
                  type="text"
                  value={inspectorName}
                  onChange={(e) => setInspectorName(e.target.value)}
                  placeholder="Your full name"
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                />
              </div>

              {/* Date */}
              <div className="mb-6">
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  <Calendar className="h-4 w-4 inline mr-1" />
                  Inspection Date
                </label>
                <input
                  type="date"
                  value={inspectionDate}
                  onChange={(e) => setInspectionDate(e.target.value)}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                />
              </div>

              {/* PPE Items List - Only show in Single Item mode */}
              {inspectionMode === 'single' && (
                <div className="space-y-2">
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Available Equipment
                  </label>
                  {loading ? (
                    <p className="text-gray-500">Loading PPE items...</p>
                  ) : ppeItems.length === 0 ? (
                    <p className="text-gray-500 text-sm">No PPE items found. Add fall protection equipment to your assets.</p>
                  ) : (
                    ppeItems.map((item) => (
                      <button
                        key={item.assetId}
                        onClick={() => {
                          setSelectedItem(item);
                          setInspectionResults({});
                        }}
                        className={`w-full text-left p-4 rounded-lg border-2 transition ${
                          selectedItem?.assetId === item.assetId
                            ? 'border-blue-600 bg-blue-50'
                            : 'border-gray-200 hover:border-blue-300'
                        }`}
                      >
                        <div className="font-semibold text-gray-900">{item.assetName}</div>
                        <div className="text-sm text-gray-600">S/N: {item.serialNumber}</div>
                        {item.lastInspection && (
                          <div className="text-xs text-gray-500 mt-1">
                            Last: {new Date(item.lastInspection).toLocaleDateString()}
                          </div>
                        )}
                        <div className={`text-xs font-semibold mt-1 ${
                          item.status === 'AVAILABLE' ? 'text-green-600' : 'text-red-600'
                        }`}>
                          {item.status}
                        </div>
                      </button>
                    ))
                  )}
                </div>
              )}
              
              {/* Production Note for Full Gear Check */}
              {inspectionMode === 'full' && (
                <div className="p-3 bg-amber-50 border border-amber-200 rounded-lg text-xs text-amber-800">
                  <p className="font-medium">ℹ️ How gear checks work</p>
                  <p className="mt-1">Full gear check records a personal PPE check; use Single Item to inspect a tracked asset from the Asset Tracker.</p>
                </div>
              )}
            </div>

            {/* Status Summary */}
            {selectedItem && status.total > 0 && (
              <div className="bg-white rounded-lg shadow-lg p-6 mt-6">
                <h3 className="font-bold text-gray-900 mb-4">Inspection Summary</h3>
                <div className="space-y-2">
                  <div className="flex items-center justify-between">
                    <span className="flex items-center gap-2 text-green-700">
                      <CheckCircle className="h-5 w-5" />
                      Pass
                    </span>
                    <span className="font-bold">{status.passCount}</span>
                  </div>
                  <div className="flex items-center justify-between">
                    <span className="flex items-center gap-2 text-yellow-700">
                      <AlertTriangle className="h-5 w-5" />
                      Warning
                    </span>
                    <span className="font-bold">{status.warnCount}</span>
                  </div>
                  <div className="flex items-center justify-between">
                    <span className="flex items-center gap-2 text-red-700">
                      <XCircle className="h-5 w-5" />
                      Fail
                    </span>
                    <span className="font-bold">{status.failCount}</span>
                  </div>
                </div>

                {(status.failCount > 0 || status.warnCount > 0) && (
                  <div className="mt-4 p-3 bg-red-50 border-2 border-red-200 rounded-lg">
                    <div className="flex items-start gap-2 text-red-800">
                      <Bell className="h-5 w-5 mt-0.5 flex-shrink-0" />
                      <div className="text-sm">
                        <div className="font-bold">Safety manager alert after saving</div>
                        <div className="text-xs mt-1">Failures and warnings are escalated to your safety manager when this save completes — delivery is confirmed in the save result</div>
                      </div>
                    </div>
                  </div>
                )}
              </div>
            )}
          </div>

          {/* RIGHT: Inspection Checklist */}
          <div className="lg:col-span-2">
            {!selectedItem && inspectionMode !== 'full' ? (
              <div className="bg-white rounded-lg shadow-lg p-12 text-center">
                <Shield className="h-16 w-16 text-gray-300 mx-auto mb-4" />
                <h3 className="text-xl font-bold text-gray-900 mb-2">Select PPE Item to Inspect</h3>
                <p className="text-gray-600">Choose an item from the list to begin inspection</p>
              </div>
            ) : (
              <div className="bg-white rounded-lg shadow-lg">
                {/* Header - Different for Full Gear Check vs Single Item */}
                <div className={`border-b border-gray-200 p-6 bg-gradient-to-r ${
                  inspectionMode === 'full' ? 'from-green-50 to-white' : 'from-blue-50 to-white'
                }`}>
                  {inspectionMode === 'full' ? (
                    <>
                      <h2 className="text-2xl font-bold text-gray-900 flex items-center gap-2">
                        <Shield className="h-7 w-7 text-green-600" />
                        Full Gear Check
                      </h2>
                      <p className="mt-2 text-sm text-gray-600">
                        Complete inspection of all personal protective equipment
                      </p>
                    </>
                  ) : selectedItem ? (
                    <>
                      <h2 className="text-2xl font-bold text-gray-900">{selectedItem.assetName || (selectedItem as any).name || 'PPE Item'}</h2>
                      <div className="mt-2 flex items-center gap-4 text-sm text-gray-600">
                        <span>Serial: {selectedItem.serialNumber || (selectedItem as any).serialNumber || 'N/A'}</span>
                        <span>•</span>
                        <span>Type: {(selectedItem.assetType || (selectedItem as any).type || 'PPE').replace(/_/g, ' ')}</span>
                      </div>
                    </>
                  ) : null}
                </div>

                {/* Inspection Points by Category */}
                <div className="p-6 space-y-8">
                  {Object.entries(groupedPoints).map(([category, points]) => (
                    <div key={category}>
                      <h3 className="text-lg font-bold text-gray-900 mb-4 border-b-2 border-blue-600 pb-2">
                        {category}
                      </h3>
                      <div className="space-y-4">
                        {points.map((point) => {
                          const result = inspectionResults[point.id];
                          return (
                            <div 
                              key={point.id}
                              className={`p-4 rounded-lg border-2 ${
                                result?.status === 'PASS' ? 'border-green-200 bg-green-50' :
                                result?.status === 'WARN' ? 'border-yellow-200 bg-yellow-50' :
                                result?.status === 'FAIL' ? 'border-red-200 bg-red-50' :
                                'border-gray-200'
                              }`}
                            >
                              <div className="flex items-start justify-between mb-3">
                                <div className="flex-1">
                                  <div className="font-semibold text-gray-900">
                                    {point.label}
                                    {point.criticality === 'critical' && (
                                      <span className="ml-2 text-xs bg-red-100 text-red-800 px-2 py-0.5 rounded-full font-bold">
                                        CRITICAL
                                      </span>
                                    )}
                                  </div>
                                </div>
                              </div>

                              {/* Status Buttons */}
                              <div className="flex gap-2 mb-3">
                                <button
                                  onClick={() => handleStatusChange(point.id, 'PASS')}
                                  className={`flex-1 px-4 py-2 rounded-lg font-semibold transition flex items-center justify-center gap-2 ${
                                    result?.status === 'PASS'
                                      ? 'bg-green-600 text-white'
                                      : 'bg-gray-100 text-gray-700 hover:bg-green-100'
                                  }`}
                                >
                                  <CheckCircle className="h-5 w-5" />
                                  Pass
                                </button>
                                <button
                                  onClick={() => handleStatusChange(point.id, 'WARN')}
                                  className={`flex-1 px-4 py-2 rounded-lg font-semibold transition flex items-center justify-center gap-2 ${
                                    result?.status === 'WARN'
                                      ? 'bg-yellow-600 text-white'
                                      : 'bg-gray-100 text-gray-700 hover:bg-yellow-100'
                                  }`}
                                >
                                  <AlertTriangle className="h-5 w-5" />
                                  Worn
                                </button>
                                <button
                                  onClick={() => handleStatusChange(point.id, 'FAIL')}
                                  className={`flex-1 px-4 py-2 rounded-lg font-semibold transition flex items-center justify-center gap-2 ${
                                    result?.status === 'FAIL'
                                      ? 'bg-red-600 text-white'
                                      : 'bg-gray-100 text-gray-700 hover:bg-red-100'
                                  }`}
                                >
                                  <XCircle className="h-5 w-5" />
                                  Fail
                                </button>
                              </div>

                              {/* Notes (show if not pass or if notes already exist) */}
                              {(result?.status === 'WARN' || result?.status === 'FAIL' || result?.notes) && (
                                <textarea
                                  value={result?.notes || ''}
                                  onChange={(e) => handleNotesChange(point.id, e.target.value)}
                                  placeholder="Describe the issue in detail..."
                                  className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-blue-500"
                                  rows={2}
                                />
                              )}
                            </div>
                          );
                        })}
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

